import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Trash2, Megaphone, Eye, EyeOff } from 'lucide-react';
import { API, PLACEMENT_LABELS } from './adminUtils';

export function AdsTab({
  showAdForm, setShowAdForm, handleAddAd, adForm, setAdForm, adPlacements,
  setAdImageFile, adSubmitting, ads, handleToggleAd, handleDeleteAd,
}) {
  return (
    <>
      <div className="flex justify-end mb-4">
        <Dialog open={showAdForm} onOpenChange={setShowAdForm}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-add-ad-btn">
              <Plus className="w-4 h-4 mr-2" /> Reklam Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Reklam Ekle</DialogTitle>
              <DialogDescription>Yerleşim seç, görsel yükle (veya URL/HTML ekle), yönlendirme linkini belirt.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddAd} className="space-y-3 mt-2">
              <div>
                <Label>Başlık (dahili)</Label>
                <Input value={adForm.title} onChange={e => setAdForm(p => ({...p, title: e.target.value}))} className="mt-1" required data-testid="admin-ad-title" />
              </div>
              <div>
                <Label>Yerleşim</Label>
                <Select value={adForm.placement} onValueChange={v => setAdForm(p => ({...p, placement: v}))}>
                  <SelectTrigger className="mt-1" data-testid="admin-ad-placement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(adPlacements.length ? adPlacements : Object.keys(PLACEMENT_LABELS)).map(k => (
                      <SelectItem key={k} value={k} data-testid={`admin-ad-place-opt-${k}`}>{PLACEMENT_LABELS[k] || k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reklam Görseli</Label>
                <Input type="file" accept="image/*" onChange={e => setAdImageFile(e.target.files?.[0] || null)} className="mt-1" data-testid="admin-ad-image-file" />
                <p className="text-xs text-slate-400 mt-1">Veya aşağıya tam URL girin / HTML içerik ekleyin</p>
                <Input value={adForm.image_url} onChange={e => setAdForm(p => ({...p, image_url: e.target.value}))} placeholder="https://..." className="mt-2" data-testid="admin-ad-image-url" />
              </div>
              <div>
                <Label>Yönlendirme URL (opsiyonel)</Label>
                <Input value={adForm.link_url} onChange={e => setAdForm(p => ({...p, link_url: e.target.value}))} placeholder="https://..." className="mt-1" data-testid="admin-ad-link" />
              </div>
              <div>
                <Label>HTML İçerik (opsiyonel, görsel yerine)</Label>
                <Textarea value={adForm.html_content} onChange={e => setAdForm(p => ({...p, html_content: e.target.value}))} rows={3} className="mt-1 resize-none font-mono text-xs" placeholder="<div>Özel HTML veya reklam ağı kodu...</div>" data-testid="admin-ad-html" />
              </div>
              <Button type="submit" disabled={adSubmitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-ad-submit">
                {adSubmitting ? 'Ekleniyor...' : 'Reklam Ekle'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3">
        {ads.map(a => (
          <div key={a._id} data-testid={`admin-ad-${a._id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-20 h-14 shrink-0 bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center">
              {a.image_url ? (
                <img src={a.image_url.startsWith('http') ? a.image_url : `${API}/files/${a.image_url}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <Megaphone className="w-6 h-6 text-slate-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 truncate">{a.title}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{PLACEMENT_LABELS[a.placement] || a.placement}</span>
                {a.link_url && <span className="truncate max-w-[200px] text-blue-600">{a.link_url}</span>}
                <span className="text-slate-400">👁 {a.impressions || 0} · 🖱 {a.clicks || 0}</span>
              </div>
            </div>
            <button
              onClick={() => handleToggleAd(a._id, a.is_active)}
              className={`p-1.5 rounded-lg transition-colors ${a.is_active ? 'text-emerald-600 bg-emerald-50' : 'text-slate-400 bg-slate-100'}`}
              title={a.is_active ? 'Aktif - tıklayınca pasifleştir' : 'Pasif - tıklayınca aktifleştir'}
              data-testid={`admin-toggle-ad-${a._id}`}
            >
              {a.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button onClick={() => handleDeleteAd(a._id)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`admin-delete-ad-${a._id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {ads.length === 0 && <div className="text-center py-8 bg-white rounded-2xl border border-slate-100 text-slate-500">Henüz reklam yok</div>}
      </div>
    </>
  );
}
